import { useEffect, useState } from 'react';
import IamIdentityModel from '@/models/entities/IamIdentityModel';
import type UserModel from '@/models/entities/UserModel';
import { listPage } from '@/models/listPage';

/**
 * Parmi ces utilisateurs, ceux liés à au moins une identité IAM (statut piloté par l'IAM) : leurs identités en une
 * requête (GET /iam_identities?user[]=…), l'API ne les donnant pas avec l'utilisateur (objets atomiques)
 */
export async function loadIamManagedUserIds(userIds: string[]): Promise<Set<number>> {
  if (!userIds.length) return new Set();
  const { models } = await listPage(IamIdentityModel, {
    filters: { user: userIds },
    page: 1,
    // Une identité par IAM qui connaît l'utilisateur : quelques-unes au plus par utilisateur
    itemsPerPage: 300,
    partial: true,
  });
  return new Set(models.map((identity) => identity.userId));
}

/** Utilisateurs d'une page de liste gérés par leur IAM ; vide tant que leurs identités ne sont pas chargées (ou en erreur) */
export function useIamManagedUserIds(users: UserModel[]): Set<number> {
  const key = users.map((user) => user.id).join(',');
  const [state, setState] = useState<{ key: string; ids: Set<number> } | null>(null);

  useEffect(() => {
    let cancelled = false;
    // Sans ses identités (erreur) : boutons laissés actifs, l'API refuse le changement d'un user géré par son IAM
    loadIamManagedUserIds(key ? key.split(',') : [])
      .catch(() => new Set<number>())
      .then((ids) => !cancelled && setState({ key, ids }));

    return () => {
      cancelled = true;
    };
  }, [key]);

  return state?.key === key ? state.ids : NO_IDS;
}

const NO_IDS = new Set<number>();
